'use client';

import {
  type ComponentProps,
  type ComponentType,
  type ReactNode,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ChevronRight, X } from 'lucide-react';
import { Slot } from 'radix-ui';

import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../shadcn/dropdown-menu';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '../shadcn/sheet';
import { useIsPhone } from './responsive-popup';

/** Matches the panel's slide-out duration. */
const PANEL_CLOSE_MS = 200;

interface MenuContextValue {
  isSheet: boolean;
  open: boolean;
  triggerRef: RefObject<HTMLButtonElement | null>;
  /** Bottom edge of the header holding the trigger; the panel starts there. */
  panelTop: number;
}

const MenuContext = createContext<MenuContextValue | null>(null);

function useMenuContext(component: string) {
  const ctx = useContext(MenuContext);
  if (!ctx)
    throw new Error(`${component} must be used within <ResponsiveMenu>`);
  return ctx;
}

/** The `<header>` around the trigger, or null when there is none. */
function findHeader(trigger: HTMLElement | null) {
  return trigger?.closest('header') ?? null;
}

interface ResponsiveMenuProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Header menu: a dropdown on larger screens; on phones a panel that slides
 * down from under the header, so it opens where the hamburger was tapped.
 * The header stays on top and the hamburger turns into ✕ while open.
 */
export function ResponsiveMenu({
  children,
  open,
  onOpenChange,
}: ResponsiveMenuProps) {
  const isSheet = useIsPhone() === true;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = open ?? uncontrolledOpen;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [panelTop, setPanelTop] = useState(0);

  const setOpen = useCallback(
    (next: boolean) => {
      if (next) {
        const trigger = triggerRef.current;
        const anchor = findHeader(trigger) ?? trigger;
        setPanelTop(anchor?.getBoundingClientRect().bottom ?? 0);
      }
      if (open === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [open, onOpenChange],
  );

  // Keep the header above the overlay and clickable (Radix disables pointer
  // events on <body> while a modal is open), so its ✕ closes the menu.
  const raised = isSheet && isOpen;
  useEffect(() => {
    const header = findHeader(triggerRef.current);
    if (!raised || !header) return;

    const { zIndex, pointerEvents } = header.style;
    header.style.zIndex = '60';
    header.style.pointerEvents = 'auto';

    return () => {
      // Stay on top until the panel has slid back under the header
      setTimeout(() => {
        header.style.zIndex = zIndex;
        header.style.pointerEvents = pointerEvents;
      }, PANEL_CLOSE_MS);
    };
  }, [raised]);

  const Root = isSheet ? Sheet : DropdownMenu;

  return (
    <MenuContext.Provider
      value={{ isSheet, open: isOpen, triggerRef, panelTop }}
    >
      <Root open={isOpen} onOpenChange={setOpen}>
        {children}
      </Root>
    </MenuContext.Provider>
  );
}

export function ResponsiveMenuTrigger({
  children,
  ...props
}: ComponentProps<typeof DropdownMenuTrigger>) {
  const { isSheet, open, triggerRef } = useMenuContext('ResponsiveMenuTrigger');
  const showClose = isSheet && open;
  const Trigger = isSheet ? SheetTrigger : DropdownMenuTrigger;

  return (
    <Trigger
      {...props}
      ref={triggerRef}
      aria-label={showClose ? 'Zamknij menu' : props['aria-label']}
    >
      {showClose ? (
        <X className="h-5 w-5 text-zinc-700 dark:text-zinc-300" />
      ) : (
        children
      )}
    </Trigger>
  );
}

interface ResponsiveMenuContentProps {
  children: ReactNode;
  /** Top block (e.g. account status), above the items. */
  header?: ReactNode;
  /** Visually hidden panel title for screen readers. */
  srTitle?: string;
  /** Dropdown alignment (desktop only). */
  align?: ComponentProps<typeof DropdownMenuContent>['align'];
  /** Dropdown classes (desktop only). */
  className?: string;
}

export function ResponsiveMenuContent({
  children,
  header,
  srTitle = 'Menu',
  align,
  className,
}: ResponsiveMenuContentProps) {
  const { isSheet, triggerRef, panelTop } = useMenuContext(
    'ResponsiveMenuContent',
  );

  if (!isSheet) {
    return (
      <DropdownMenuContent align={align} className={className}>
        {header}
        {children}
      </DropdownMenuContent>
    );
  }

  return (
    <SheetContent
      side="top"
      showCloseButton={false}
      aria-describedby={undefined}
      overlayClassName="bg-black/40"
      overlayStyle={{ top: panelTop }}
      onOpenAutoFocus={(e) => {
        // Don't highlight the first item; focus the panel itself instead
        e.preventDefault();
        (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
      }}
      onPointerDownOutside={(e) => {
        // The trigger (now ✕) toggles the menu itself; dismissing here
        // first would make its click reopen it
        if (triggerRef.current?.contains(e.target as Node)) e.preventDefault();
      }}
      style={{
        top: panelTop,
        maxHeight: `calc(100dvh - ${panelTop}px - 2rem)`,
      }}
      className="flex flex-col gap-0 rounded-b-xl border-b-2 border-zinc-200 p-0 pb-3 shadow-[0_3px_0_0_#e4e4e7] outline-none data-[state=closed]:duration-200 data-[state=open]:duration-300 dark:border-zinc-700 dark:shadow-[0_3px_0_0_#3f3f46]"
    >
      <SheetTitle className="sr-only">{srTitle}</SheetTitle>
      {header && <div className="shrink-0 px-5 pt-4">{header}</div>}
      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto overscroll-contain px-3',
          !header && 'pt-2',
        )}
      >
        {children}
      </div>
    </SheetContent>
  );
}

interface ResponsiveMenuItemProps {
  children: ReactNode;
  /** Merge into the single child (e.g. a `Link`) instead of a `<button>`. */
  asChild?: boolean;
  icon?: ComponentType<{ className?: string }>;
  /** Called on activation; the menu closes afterwards. */
  onSelect?: () => void;
  className?: string;
}

export function ResponsiveMenuItem({
  children,
  asChild = false,
  icon: Icon,
  onSelect,
  className,
}: ResponsiveMenuItemProps) {
  const { isSheet } = useMenuContext('ResponsiveMenuItem');
  const Comp = asChild ? Slot.Root : 'button';

  if (!isSheet) {
    return (
      <DropdownMenuItem
        asChild
        onSelect={onSelect}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2',
          className,
        )}
      >
        <Comp {...(asChild ? {} : { type: 'button' as const })}>
          {Icon && <Icon className="h-4 w-4" />}
          <Slot.Slottable>{children}</Slot.Slottable>
        </Comp>
      </DropdownMenuItem>
    );
  }

  return (
    <SheetClose asChild>
      <Comp
        {...(asChild ? {} : { type: 'button' as const })}
        onClick={onSelect}
        className={cn(
          'flex h-12 w-full items-center gap-3 rounded-md px-2 text-left text-[15px] text-zinc-800 transition-colors hover:bg-zinc-50 active:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800/60 dark:active:bg-zinc-800',
          className,
        )}
      >
        {Icon && <Icon className="h-5 w-5 shrink-0 text-zinc-500" />}
        <Slot.Slottable>{children}</Slot.Slottable>
        <ChevronRight
          className="ml-auto h-4 w-4 shrink-0 text-zinc-300 dark:text-zinc-600"
          aria-hidden
        />
      </Comp>
    </SheetClose>
  );
}

export function ResponsiveMenuSeparator() {
  const { isSheet } = useMenuContext('ResponsiveMenuSeparator');
  if (!isSheet) return <DropdownMenuSeparator />;

  return (
    <div role="separator" className="my-2 h-px bg-zinc-100 dark:bg-zinc-800" />
  );
}
